/**
 * Upload Signature Creator Component
 *
 * Allows consultants to upload an image file as their signature.
 * Supports PNG and JPEG formats with drag-and-drop functionality.
 */

import { useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Box,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Typography,
    Paper,
    Grid,
    FormControlLabel,
    Checkbox,
    CircularProgress,
    Alert,
} from '@mui/material';
import Button from 'views/Dashboards-B2B/components/Button';
import { CloudUpload, Delete } from '@mui/icons-material';
import { AppDispatch } from 'redux/store';
import { createSignature } from 'redux/signatures/signatures.middleware';
import { selectSignaturesCreating } from 'redux/signatures/signatures.slice';
import {
    SIGNATURE_SIZES,
    SIGNATURE_DEFAULTS,
    SIGNATURE_CONSTRAINTS,
    validateImageFile,
    fileToBase64,
} from 'config/signatureFonts';
import type { SignatureCreateRequest } from 'types/signature';

interface UploadSignatureCreatorProps {
    onSuccess?: () => void;
}

export default function UploadSignatureCreator({ onSuccess }: UploadSignatureCreatorProps) {
    const dispatch = useDispatch<AppDispatch>();
    const isCreating = useSelector(selectSignaturesCreating);

    const fileInputRef = useRef<HTMLInputElement>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [uploadedFile, setUploadedFile] = useState<File | null>(null);
    const [previewURL, setPreviewURL] = useState<string>('');

    const [formData, setFormData] = useState({
        size: SIGNATURE_DEFAULTS.size as 'small' | 'medium' | 'large',
        display_name: '',
        title: '',
        signature_name: '',
        is_default: false,
    });

    const [errors, setErrors] = useState<Record<string, string>>({});

    const handleFileSelect = async (file: File) => {
        // Validate file
        const validation = validateImageFile(file);
        if (!validation.valid) {
            setErrors({ file: validation.error || 'Invalid file' });
            return;
        }

        // Clear previous errors
        setErrors({});

        // Set file and create preview
        setUploadedFile(file);
        const reader = new FileReader();
        reader.onload = (e) => {
            setPreviewURL(e.target?.result as string);
        };
        reader.readAsDataURL(file);
    };

    const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            handleFileSelect(file);
        }
    };

    const handleDragEnter = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        const file = e.dataTransfer.files?.[0];
        if (file) {
            handleFileSelect(file);
        }
    };

    const handleRemoveFile = () => {
        setUploadedFile(null);
        setPreviewURL('');
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleChange = (field: string, value: string | boolean | File | null) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors[field];
                return newErrors;
            });
        }
    };

    const validate = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (!uploadedFile) {
            newErrors.file = 'Please upload a signature image';
        }

        if (
            formData.display_name &&
            formData.display_name.length > SIGNATURE_CONSTRAINTS.MAX_DISPLAY_NAME_LENGTH
        ) {
            newErrors.display_name = `Display name must be less than ${SIGNATURE_CONSTRAINTS.MAX_DISPLAY_NAME_LENGTH} characters`;
        }

        if (formData.title && formData.title.length > SIGNATURE_CONSTRAINTS.MAX_TITLE_LENGTH) {
            newErrors.title = `Title must be less than ${SIGNATURE_CONSTRAINTS.MAX_TITLE_LENGTH} characters`;
        }

        if (
            formData.signature_name &&
            formData.signature_name.length > SIGNATURE_CONSTRAINTS.MAX_SIGNATURE_NAME_LENGTH
        ) {
            newErrors.signature_name = `Signature name must be less than ${SIGNATURE_CONSTRAINTS.MAX_SIGNATURE_NAME_LENGTH} characters`;
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate() || !uploadedFile) {
            return;
        }

        try {
            // Convert file to base64
            const base64Data = await fileToBase64(uploadedFile);

            const payload: SignatureCreateRequest = {
                creation_method: 'uploaded',
                uploaded_image_base64: base64Data,
                uploaded_image_mime_type: uploadedFile.type,
                size: formData.size,
                display_name: formData.display_name.trim() || undefined,
                title: formData.title.trim() || undefined,
                signature_name: formData.signature_name.trim() || undefined,
                is_default: formData.is_default,
            };

            await dispatch(createSignature(payload)).unwrap();

            // Reset form
            handleRemoveFile();
            setFormData({
                size: SIGNATURE_DEFAULTS.size as 'small' | 'medium' | 'large',
                display_name: '',
                title: '',
                signature_name: '',
                is_default: false,
            });

            onSuccess?.();
        } catch (error) {
            console.error('Failed to create signature:', error);
        }
    };

    return (
        <Box>
            <Grid container spacing={3}>
                {/* Upload Section */}
                <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Typography variant="subtitle1" gutterBottom>
                            Upload Signature Image
                        </Typography>

                        <Paper
                            elevation={0}
                            onDragEnter={handleDragEnter}
                            onDragLeave={handleDragLeave}
                            onDragOver={handleDragOver}
                            onDrop={handleDrop}
                            sx={{
                                p: 4,
                                border: `2px dashed ${isDragging ? '#1976d2' : '#e0e0e0'}`,
                                borderRadius: 2,
                                backgroundColor: isDragging ? '#f0f7ff' : '#f9f9f9',
                                textAlign: 'center',
                                cursor: 'pointer',
                                transition: 'all 0.3s',
                                '&:hover': {
                                    borderColor: '#1976d2',
                                    backgroundColor: '#f0f7ff',
                                },
                            }}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            {previewURL ? (
                                <Box>
                                    <img
                                        src={previewURL}
                                        alt="Signature preview"
                                        style={{
                                            maxWidth: '100%',
                                            maxHeight: 200,
                                            objectFit: 'contain',
                                        }}
                                    />
                                    <Typography variant="caption" display="block" sx={{ mt: 2 }}>
                                        {uploadedFile?.name}
                                    </Typography>
                                </Box>
                            ) : (
                                <Box>
                                    <CloudUpload sx={{ fontSize: 48, color: '#999', mb: 2 }} />
                                    <Typography variant="body1" gutterBottom>
                                        Drag and drop your signature image here
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        or click to browse
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ mt: 1, display: 'block' }}
                                    >
                                        Supported: PNG, JPEG (max{' '}
                                        {SIGNATURE_CONSTRAINTS.MAX_IMAGE_SIZE_MB}MB)
                                    </Typography>
                                </Box>
                            )}
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept={SIGNATURE_CONSTRAINTS.ALLOWED_IMAGE_TYPES.join(',')}
                                onChange={handleFileInputChange}
                                style={{ display: 'none' }}
                            />
                        </Paper>

                        {errors.file && <Alert severity="error">{errors.file}</Alert>}

                        {uploadedFile && (
                            <Button
                                border="purple"
                                style={{
                                    background: 'transparent',
                                    borderColor: '#dc2626',
                                    color: '#dc2626',
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveFile();
                                }}
                            >
                                <Delete style={{ marginRight: '8px' }} />
                                Remove Image
                            </Button>
                        )}

                        <FormControl fullWidth>
                            <InputLabel>Display Size</InputLabel>
                            <Select
                                value={formData.size}
                                label="Display Size"
                                onChange={(e) => handleChange('size', e.target.value)}
                            >
                                {SIGNATURE_SIZES.map((size) => (
                                    <MenuItem key={size.value} value={size.value}>
                                        {size.name} {size.description && `- ${size.description}`}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                </Grid>

                {/* Metadata */}
                <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Typography variant="subtitle1" gutterBottom>
                            Signature Details (Optional)
                        </Typography>

                        <TextField
                            label="Display Name"
                            placeholder="e.g., Dr. John Smith"
                            value={formData.display_name}
                            onChange={(e) => handleChange('display_name', e.target.value)}
                            error={!!errors.display_name}
                            helperText={errors.display_name || 'Override your name in agreements'}
                            fullWidth
                        />

                        <TextField
                            label="Professional Title"
                            placeholder="e.g., RCIC, Immigration Consultant"
                            value={formData.title}
                            onChange={(e) => handleChange('title', e.target.value)}
                            error={!!errors.title}
                            helperText={errors.title || 'Credentials displayed below signature'}
                            fullWidth
                        />

                        <TextField
                            label="Signature Name"
                            placeholder="e.g., Formal Signature"
                            value={formData.signature_name}
                            onChange={(e) => handleChange('signature_name', e.target.value)}
                            error={!!errors.signature_name}
                            helperText={errors.signature_name || 'Label to identify this signature'}
                            fullWidth
                        />

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={formData.is_default}
                                    onChange={(e) => handleChange('is_default', e.target.checked)}
                                />
                            }
                            label="Set as default signature"
                        />

                        <Alert severity="info" sx={{ mt: 2 }}>
                            <Typography variant="caption">
                                <strong>Best Practices:</strong>
                                <br />
                                • Use a transparent background (PNG)
                                <br />
                                • Ensure good contrast with white background
                                <br />• Keep the image dimensions reasonable (300x100px recommended)
                            </Typography>
                        </Alert>

                        <Button
                            background="purple"
                            onClick={handleSubmit}
                            disabled={isCreating || !uploadedFile}
                        >
                            {isCreating && (
                                <CircularProgress size={20} style={{ marginRight: '8px' }} />
                            )}
                            {isCreating ? 'Creating...' : 'Create Signature'}
                        </Button>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
}
